/**
 * ReportHeader - Health Monthly Report header with company info, patient details, and QR code.
 *
 * Layout:
 * - Top row: Company logo + name + address (left) | QR code (right)
 * - Bottom row: Patient details table (name, ID, DOB, gender, nationality, address, care owner)
 *
 * Requirements: 17.2
 */

import React from 'react';
import moment from 'moment';

export interface CompanyInfo {
  companyName: string;
  companyCode: string;
  companyLogo: string;
  companyAddress1: string;
  companyAddress2: string;
  companyAddress3?: string;
  telephone: string;
  email: string;
}

export interface PatientInfo {
  name: string;
  id: string;
  identification: string; // NRIC or Passport
  dob: string;
  age: string;
  gender: string;
  nationality: string;
  address: string;
  mobile: string;
  careOwner: string;
  centre: string;
}

export interface ReportHeaderProps {
  header: {
    patientName: string;
    patientId: string;
    reportMonth: string;
    generatedAt: string;
  };
  companyInfo?: CompanyInfo;
  patientInfo?: PatientInfo;
  qrCodeDataUrl?: string;
  qrCodeUrl?: string;
  recorderNames?: { name: string; count: number }[];
}

const ReportHeader: React.FC<ReportHeaderProps> = ({ header, companyInfo, patientInfo, qrCodeDataUrl, recorderNames }) => {
  const formattedTimestamp = moment(header.generatedAt).isValid()
    ? moment(header.generatedAt).format('DD MMM YYYY, h:mm A')
    : header.generatedAt;

  const labelStyle: React.CSSProperties = {
    fontSize: '0.78rem',
    color: '#7f8c8d',
    fontWeight: 600,
    whiteSpace: 'nowrap',
    paddingRight: '0.5rem',
    verticalAlign: 'top',
    paddingBottom: '4px',
  };

  const valueStyle: React.CSSProperties = {
    fontSize: '0.82rem',
    color: '#2c3e50',
    fontWeight: 700,
    paddingBottom: '4px',
    verticalAlign: 'top',
  };

  return (
    <header
      className="report-header"
      style={{
        borderBottom: '2px solid #2c3e50',
        paddingBottom: '1rem',
        marginBottom: '1.5rem',
      }}
    >
      {/* Top row: Company + Report Title + QR */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1rem' }}>
        {/* Left: Company Info */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          {companyInfo?.companyLogo && (
            <img
              src={companyInfo.companyLogo}
              alt={companyInfo.companyName}
              style={{ height: '90px', objectFit: 'contain' }}
            />
          )}
          <div>
            <h2 style={{ margin: 0, fontSize: '1rem', fontWeight: 700, color: '#2c3e50' }}>
              MONTHLY HEALTH REPORT
            </h2>
            <p style={{ margin: 0, fontSize: '0.78rem', color: '#34495e', fontWeight: 600 }}>
              {companyInfo?.companyName || ''}
            </p>
            {companyInfo?.companyCode && (
              <p style={{ margin: 0, fontSize: '0.7rem', color: '#7f8c8d' }}>{companyInfo.companyCode}</p>
            )}
            {companyInfo?.companyAddress1 && (
              <p style={{ margin: 0, fontSize: '0.68rem', color: '#7f8c8d' }}>
                {companyInfo.companyAddress1} {companyInfo.companyAddress2}
              </p>
            )}
            {companyInfo?.telephone && (
              <p style={{ margin: 0, fontSize: '0.68rem', color: '#7f8c8d' }}>
                Tel: {companyInfo.telephone}
              </p>
            )}
          </div>
        </div>

        {/* Right: QR Code */}
        {qrCodeDataUrl && (
          <div style={{ textAlign: 'center' }}>
            <img
              src={qrCodeDataUrl}
              alt="Health Tracker QR"
              style={{ width: '120px', height: '120px' }}
            />
            <p style={{ margin: '2px 0 0 0', fontSize: '0.6rem', color: '#7f8c8d' }}>SCAN HERE</p>
            <p style={{ margin: '0px 0 0 0', fontSize: '0.6rem', color: '#7f8c8d' }}>(TRACK LIVE RECORD)</p>
          </div>
        )}
      </div>

      {/* Patient Details Table - 50:50 ratio */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2rem' }}>
        {/* Left column */}
        <table style={{ borderCollapse: 'collapse', width: '100%' }}>
          <tbody>
            <tr>
              <td style={labelStyle}>Patient Name</td>
              <td style={valueStyle}>: {patientInfo?.name || header.patientName || '-'}</td>
            </tr>
            <tr>
              <td style={labelStyle}>Identification</td>
              <td style={valueStyle}>: {patientInfo?.identification || '-'}</td>
            </tr>
            <tr>
              <td style={labelStyle}>Date of Birth</td>
              <td style={valueStyle}>: {patientInfo?.dob || '-'}</td>
            </tr>
            <tr>
              <td style={labelStyle}>Age / Gender</td>
              <td style={valueStyle}>: {patientInfo?.age || '-'} - {patientInfo?.gender || '-'}</td>
            </tr>
            <tr>
              <td style={labelStyle}>Nationality</td>
              <td style={valueStyle}>: {patientInfo?.nationality || '-'}</td>
            </tr>
          </tbody>
        </table>

        {/* Right column */}
        <table style={{ borderCollapse: 'collapse', width: '100%' }}>
          <tbody>
            <tr>
              <td style={labelStyle}>Centre</td>
              <td style={valueStyle}>: {patientInfo?.centre || '-'}</td>
            </tr>
            <tr>
              <td style={labelStyle}>Care Owner</td>
              <td style={valueStyle}>: {patientInfo?.careOwner || '-'}</td>
            </tr>
            <tr>
              <td style={labelStyle}>Contact</td>
              <td style={valueStyle}>: {patientInfo?.mobile || '-'}</td>
            </tr>
            <tr>
              <td style={labelStyle}>Report Period</td>
              <td style={valueStyle}>: {header.reportMonth}</td>
            </tr>
            <tr>
              <td style={labelStyle}>Generated</td>
              <td style={valueStyle}>: {formattedTimestamp}</td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Recorder Names - badge list */}
      {recorderNames && recorderNames.length > 0 && (
        <div style={{ marginTop: '0.05rem', paddingTop: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
          <span style={{ fontSize: '0.78rem', color: '#7f8c8d', fontWeight: 600, marginRight: '0.25rem' }}>Recorded By :</span>
          {recorderNames.map((recorder) => (
            <span
              key={recorder.name}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '5px',
                padding: '3px 10px',
                borderRadius: '12px',
                fontSize: '0.72rem',
                fontWeight: 600,
                color: '#5A46AA',
                backgroundColor: 'rgba(90, 70, 170, 0.08)',
                border: '1px solid rgba(90, 70, 170, 0.2)',
              }}
            >
              {recorder.name}
              <span
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  minWidth: '18px',
                  height: '18px',
                  borderRadius: '9px',
                  fontSize: '0.65rem',
                  fontWeight: 700,
                  color: '#5A46AA',
                  backgroundColor: '#fff',
                  border: '1px solid rgba(90, 70, 170, 0.3)',
                  padding: '0 4px',
                }}
              >
                {recorder.count}
              </span>
            </span>
          ))}
        </div>
      )}
    </header>
  );
};

export default ReportHeader;
